/**
 * Which words of a text the spell check looks at. DOM-free and worker-free, so
 * every app's editor, the worker protocol and the tests share it.
 *
 * Offsets are UTF-16 code units of the text handed in. An app whose text has
 * positions of its own (Write's editor offsets: one position per break,
 * picture or field) maps its content to a string with the same offsets first,
 * and puts U+FFFC wherever a position is not text — it is never part of a word.
 */

/** One checkable word of a text. */
export interface SpellToken {
  /** First offset of the word. */
  readonly start: number;
  /** Offset after the word. */
  readonly end: number;
  /** The word as it stands in the text. */
  readonly word: string;
  /**
   * What the dictionary is asked, in order; the word is spelled correctly when
   * any of them is. A word followed by a full stop is also asked with it
   * („usw.", „z. B."), because the dictionaries list abbreviations that way.
   */
  readonly candidates: readonly string[];
}

/** A run of text between whitespace and object positions. */
const CHUNK = /[^\s￼]+/gu;
/** Letters and digits, with apostrophes inside a word („geht's", "don't"). */
const WORD = /[\p{L}\p{M}\p{N}_]+(?:['’][\p{L}\p{M}\p{N}_]+)*/gu;
const HAS_DIGIT_OR_UNDERSCORE = /[\p{N}_]/u;
const URL_SCHEME = /^[("'„“‚‘<[]*(?:https?|ftp|file|mailto|tel):/i;
const WWW = /^[("'„“‚‘<[]*www\./i;
/** A bare host name such as `example.com` or `mail.example.de/path`. */
const HOST = /^[("'„“‚‘<[]*(?:[a-z0-9-]+\.)+[a-z]{2,}(?:[/:?#]\S*)?[)"'“‘>\],.;:!?]*$/;
const EMAIL = /[\p{L}\p{N}._%+-]@[\p{L}\p{N}-]+\.[\p{L}\p{N}.-]/u;

/** Whether a whitespace-delimited chunk is an address rather than prose. */
export function isAddressChunk(chunk: string): boolean {
  return (
    chunk.includes('://') ||
    URL_SCHEME.test(chunk) ||
    WWW.test(chunk) ||
    EMAIL.test(chunk) ||
    HOST.test(chunk)
  );
}

/**
 * Whether a word is left alone whatever the dictionary says: anything with a
 * digit or underscore (`A4`, `3D`, `foo_bar`), a single letter, and short
 * all-caps words — those are acronyms (`GmbH` is not all-caps, `USA`, `PDF`
 * and `ERP` are) and no dictionary lists them all.
 */
export function isSkippedWord(word: string): boolean {
  if (HAS_DIGIT_OR_UNDERSCORE.test(word)) return true;
  const letters = [...word.replace(/['’]/g, '')];
  if (letters.length < 2) return true;
  const upper = word.toUpperCase();
  if (word === upper && word !== word.toLowerCase() && letters.length <= 4) return true;
  return false;
}

/** The checkable words of a paragraph's spell text, in reading order. */
export function spellTokens(text: string): SpellToken[] {
  const tokens: SpellToken[] = [];
  for (const chunk of text.matchAll(CHUNK)) {
    const chunkText = chunk[0];
    if (isAddressChunk(chunkText)) continue;
    const chunkStart = chunk.index;
    for (const match of chunkText.matchAll(WORD)) {
      const word = match[0];
      if (isSkippedWord(word)) continue;
      const start = chunkStart + match.index;
      const end = start + word.length;
      // The dictionaries spell the apostrophe straight; Word types it curly.
      const asked = word.replace(/’/g, "'");
      const candidates = text[end] === '.' ? [asked, `${asked}.`] : [asked];
      tokens.push({ start, end, word, candidates });
    }
  }
  return tokens;
}

/**
 * Whether a word is in the personal dictionary. It also counts at the start of
 * a sentence: a listed `foo` covers `Foo`, as Hunspell does for its own words.
 */
export function inWordList(words: ReadonlySet<string>, word: string): boolean {
  if (words.has(word)) return true;
  const first = word.charAt(0);
  if (first !== first.toUpperCase()) return false;
  return words.has(first.toLowerCase() + word.slice(1));
}
